/**
 * Calendar-day helpers that never go through `Date` parsing of `YYYY-MM-DD`
 * (UTC midnight) — same reason as `formatIsoDate`.
 */

export type IsoDay = { year: number; month: number; day: number };

export type CalendarCell = {
  iso: string;
  day: number;
  inMonth: boolean;
};

export function parseIsoDay(iso: string): IsoDay | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

export function toIsoDay(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function todayIsoDay(now = new Date()): string {
  return toIsoDay(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/**
 * Six-week Sunday-first grid for `month` (1–12), including leading/trailing
 * days of the neighbouring months.
 */
export function calendarGrid(year: number, month: number): CalendarCell[] {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysThis = new Date(year, month, 0).getDate();
  const daysPrev = new Date(year, month - 1, 0).getDate();
  const prev = addMonths(year, month, -1);
  const next = addMonths(year, month, 1);
  const cells: CalendarCell[] = [];

  for (let i = firstWeekday - 1; i >= 0; i--) {
    const day = daysPrev - i;
    cells.push({ iso: toIsoDay(prev.year, prev.month, day), day, inMonth: false });
  }
  for (let day = 1; day <= daysThis; day++) {
    cells.push({ iso: toIsoDay(year, month, day), day, inMonth: true });
  }
  let nextDay = 1;
  while (cells.length < 42) {
    cells.push({ iso: toIsoDay(next.year, next.month, nextDay), day: nextDay, inMonth: false });
    nextDay += 1;
  }
  return cells;
}

export function isoDayInRange(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}
